import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {Application, Client, InputTypeEnum, Tag} from 'src/app/models';
import {DataService, IndividualsService, SnackbarService} from 'src/app/services';

@Component({
    selector: 'app-tag-edit',
    templateUrl: './tag-info-popup.component.html',
    styleUrls: ['./tag-info-popup.component.scss']
})
export class TagInfoPopupComponent implements OnInit {
    tag: Tag;
    ready: boolean = false;
    options: string = '';
    optionsNumber: number;
    DROPDOWN: InputTypeEnum = InputTypeEnum.DROPDOWN;
    RADIO: InputTypeEnum = InputTypeEnum.RADIO;
    TEXTBOX: InputTypeEnum = InputTypeEnum.TEXTBOX;
    TEXTAREA: InputTypeEnum = InputTypeEnum.TEXTAREA;
    SENTBY: InputTypeEnum = InputTypeEnum.SENTBY;
    CLIENTS: InputTypeEnum = InputTypeEnum.CLIENTS;
    APPLICATIONS: InputTypeEnum = InputTypeEnum.APPLICATIONS;
    DATE: InputTypeEnum = InputTypeEnum.DATE;
    CONFIRM: InputTypeEnum = InputTypeEnum.CONFIRM;

    applications: Application[] = null;
    appSelected = false;
    selectedAppName;
    appList = [];
    applicationChange = new Subject<string>();

    clientChange = new Subject<string>();
    clients: Client[] = [];
    currentClient;
    clientsImpacted = [];

    senders = null;
    sentBy: string;
    senderChange = new Subject<string>();

    constructor(public dialog: MatDialogRef<TagInfoPopupComponent>,
                private individualService: IndividualsService,
                private dataService: DataService,
                public snackbarService: SnackbarService,
                @Inject(MAT_DIALOG_DATA) public data: any) {
        this.tag = new Tag(this.data.id, this.data.template_id, this.data.name, this.data.tag_text, this.data.required, this.data.template_tag, this.data.tag_options, this.data.prompt, this.data.input_type);
    }

    ngOnInit() {
        const delay = 750;
        this.applicationChange.pipe(debounceTime(delay), distinctUntilChanged()).subscribe((term: string) => this.getApplications(term));
        this.clientChange.pipe(debounceTime(delay), distinctUntilChanged()).subscribe((term: string) => this.getClientNames(term));
        this.senderChange.pipe(debounceTime(delay), distinctUntilChanged()).subscribe((term: string) => this.getSenders(term));

        this.dataService.getUserInfo().subscribe(resi => this.sentBy = resi['name']);

        this.tag.input_type = this.tag.input_type == undefined ? InputTypeEnum.TEXTAREA : this.tag.input_type;
        this.ready = true;
    }

    optionsDisabled(): boolean {
        return !(this.tag.input_type === InputTypeEnum.TEXTBOX || this.tag.input_type === InputTypeEnum.TEXTAREA);
    }

    addOptions() {
        this.tag.tag_options = this.tag.tag_options != '' ? this.tag.tag_options : this.options;
    }

    removeOptions() {
        this.options = this.tag.tag_options;
        this.tag.tag_options = '';
    }

    isValid() {
        if (this.isCustomTag(this.tag)) {
            return true;
        }
        if (this.tag.input_type == InputTypeEnum.RADIO || this.tag.input_type == InputTypeEnum.DROPDOWN) {
            if (this.tag.tag_options === '' || this.tag.prompt === '') {
                return false;
            }
        }
        return true;
    }

    isCustomTag(tag: Tag): boolean {
        return [InputTypeEnum.DATE, InputTypeEnum.CONFIRM, InputTypeEnum.APPLICATIONS, InputTypeEnum.CLIENTS, InputTypeEnum.SENTBY].includes(tag.input_type);
    }

    getApplications(filterValue: string): void {
        filterValue = filterValue && filterValue.toLowerCase ? filterValue.toLowerCase() : '';
        if (filterValue != null && filterValue.length > 1) {
            this.dataService.getApplicationNames(filterValue).subscribe({
                next: applications => this.applications = applications,
                error: error => this.snackbarService.openSnackBar(error, true)
            });
        } else {
            this.applications = [];
        }
    }

    appFieldBlur(event?: any, input?: any): void {
        setTimeout(() => {
            if (this.selectedAppName != null && this.selectedAppName.trim() !== '') {
                this.addApp(event, input);
            }
        }, 150);
    }

    addApp(event?: any, appInput?): void {
        this.applications = [];
        this.applicationChange.next('');
        const value = event.value ? event.value : event.application_name;

        if (value === undefined) {
            if (this.selectedAppName != null && !this.appList.includes(this.selectedAppName.trim())) {
                if (this.selectedAppName !== '') {
                    this.appList.push(this.selectedAppName.trim());
                }
                this.selectedAppName = '';
                this.appSelected = true;
            } else {
                this.snackbarService.openSnackBar('Duplicate Application provided - try again ', true);
            }
        } else {
            if (this.isDuplicateApp(value) === true) {
                this.snackbarService.openSnackBar('Duplicate Application provided - try again ', true);
            } else {
                this.appList.push(value);
                this.selectedAppName = '';
                this.appSelected = false;
            }
        }

        this.selectedAppName = '';

        if (appInput) {
            appInput.value = '';
        }
        if (event.value) {
            event.value = '';
        }
    }

    isDuplicateApp(appName): boolean {
        const found = this.appList.find(p => p === appName);
        return Boolean(found);
    }

    getAppName(app): string {
        return app ? app.application_name : '';
    }

    removeApp(appName): void {
        this.appList = this.appList.filter((p: any) => (appName !== p));
    }

    getClientNames(filterValueClient: string): void {
        if (filterValueClient != null && filterValueClient.length > 1) {
            this.dataService.getClientNames(filterValueClient).subscribe({
                next: clients => this.clients = clients,
                error: error => this.snackbarService.openSnackBar(error, true)
            });
        } else {
            this.clients = [];
        }
    }

    removeClient(clientName): void {
        this.clientsImpacted = this.clientsImpacted.filter((p: any) => (clientName !== p));
    }

    getClientName(client): string {
        return client ? client.client_name : '';
    }

    clientFieldBlur(event?: any, input?: any): void {
        setTimeout(() => {
            if (this.currentClient != null && this.currentClient.trim() !== '') {
                this.addClient(event, input);
            }
        }, 150);
    }

    addClient(event?: any, clientInput?): void {
        this.clientChange.next('');
        this.clients = [];
        const value = event.client_name;

        if (value === undefined && this.currentClient != null) {
            if (this.clientsImpacted.includes(this.currentClient.trim())) {
                this.snackbarService.openSnackBar('Duplicate Client provided - try again ', true);
            } else {
                this.clientsImpacted.push(this.currentClient.trim());
                this.currentClient = '';
            }
        } else {
            if (this.isDuplicateClient(event.client_name)) {
                this.snackbarService.openSnackBar('Duplicate Client provided - try again ', true);
            } else {
                this.clientsImpacted.push(value);
                this.currentClient = '';
            }
        }

        this.currentClient = '';
        if (clientInput) {
            clientInput.value = '';
        }
        if (event.value) {
            event.value = '';
        }
    }

    isDuplicateClient(client: void): boolean {
        const found = this.clientsImpacted.find(p => p === client);
        return Boolean(found);
    }

    getSenders(filterValue: string): void {
        filterValue = filterValue && filterValue.toLowerCase ? filterValue.toLowerCase() : '';
        if (filterValue != null && filterValue.length > 1) {
            this.individualService.getIndividuals(filterValue).subscribe({
                next: individualsAndDevices => this.senders = individualsAndDevices,
                error: error => this.snackbarService.openSnackBar(error, true)
            });
        } else {
            this.senders = [];
        }
    }
}
